/**
 * Evidence Upload Helper
 * 
 * Centralized evidence upload for inspection items
 * Uses existing dvi_media bucket with standard path format
 */

import { supabase } from '@/integrations/supabase/client';
// DTC-L: route evidence-upload `captured_at` write through clock.
import { clock } from '@/utils/clock';

export interface UploadEvidenceParams {
  /** Shop ID (derived from inspection record) */
  shopId: string;
  /** Ticket ID */
  ticketId: string;
  /** Inspection item ID to attach evidence to */
  inspectionItemId: string;
  /** File to upload */
  file: File;
  /** Source of the evidence */
  source: 'intake' | 'technician';
  /** Shared key for cross-referencing (e.g., 'DASH_PHOTO') */
  sharedKey?: string;
  /** Item key for reference */
  itemKey: string;
}

export interface EvidenceRecord {
  id: string;
  shop_id: string;
  ticket_inspection_item_id: string;
  evidence_type: 'photo' | 'video' | 'document';
  storage_path: string;
  mime_type: string;
  size_bytes: number;
  captured_by: string;
  captured_at: string;
  meta: Record<string, unknown>;
}

/**
 * Determine evidence type from MIME type
 */
function getEvidenceType(mimeType: string): 'photo' | 'video' | 'document' {
  if (mimeType.startsWith('image/')) return 'photo';
  if (mimeType.startsWith('video/')) return 'video';
  return 'document';
}

/**
 * Generate a unique filename with extension
 */
function generateFilename(file: File): string {
  const ext = file.name.split('.').pop() || 'jpg';
  const uuid = crypto.randomUUID();
  return `${uuid}.${ext}`;
}

/**
 * Upload evidence for an inspection item
 * 
 * @param params - Upload parameters
 * @returns The created evidence record
 * @throws Error if upload or insert fails
 */
export async function uploadInspectionEvidence(
  params: UploadEvidenceParams
): Promise<EvidenceRecord> {
  const {
    shopId,
    ticketId,
    inspectionItemId,
    file,
    source,
    sharedKey,
    itemKey,
  } = params;

  // Get current user
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('Not authenticated');
  }

  // Generate storage path: {shopId}/{ticketId}/{uuid}.{ext}
  const filename = generateFilename(file);
  const storagePath = `${shopId}/${ticketId}/${filename}`;

  // Upload to dvi_media bucket (reuse existing bucket)
  const { error: uploadError } = await supabase.storage
    .from('dvi_media')
    .upload(storagePath, file, {
      contentType: file.type,
      upsert: false,
    });

  if (uploadError) {
    console.error('Evidence upload failed:', uploadError);
    throw new Error(`Failed to upload evidence: ${uploadError.message}`);
  }

  // Insert evidence record
  const evidenceRecord = {
    shop_id: shopId,
    ticket_inspection_item_id: inspectionItemId,
    evidence_type: getEvidenceType(file.type),
    storage_path: storagePath,
    mime_type: file.type,
    size_bytes: file.size,
    captured_by: user.id,
    captured_at: clock.now().toISOString(),
    meta: {
      source,
      shared_key: sharedKey,
      item_key: itemKey,
      original_filename: file.name,
    },
  };

  const { data, error: insertError } = await supabase
    .from('ticket_inspection_evidence')
    .insert(evidenceRecord)
    .select()
    .single();

  if (insertError) {
    console.error('Evidence record insert failed:', insertError);
    // Try to clean up the uploaded file
    await supabase.storage.from('dvi_media').remove([storagePath]);
    throw new Error(`Failed to create evidence record: ${insertError.message}`);
  }

  return data as EvidenceRecord;
}

/**
 * Delete evidence by ID (also removes from storage)
 */
export async function deleteInspectionEvidence(
  evidenceId: string
): Promise<void> {
  // First get the evidence record to get storage path
  const { data: evidence, error: fetchError } = await supabase
    .from('ticket_inspection_evidence')
    .select('storage_path')
    .eq('id', evidenceId)
    .single();

  if (fetchError) {
    throw new Error(`Failed to fetch evidence: ${fetchError.message}`);
  }

  // Delete from storage
  if (evidence?.storage_path) {
    await supabase.storage.from('dvi_media').remove([evidence.storage_path]);
  }

  // Delete record
  const { error: deleteError } = await supabase
    .from('ticket_inspection_evidence')
    .delete()
    .eq('id', evidenceId);

  if (deleteError) {
    throw new Error(`Failed to delete evidence: ${deleteError.message}`);
  }
}

/**
 * Get signed URL for evidence (for display)
 */
export async function getEvidenceSignedUrl(
  storagePath: string,
  expiresIn: number = 3600
): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from('dvi_media')
    .createSignedUrl(storagePath, expiresIn);

  if (error) {
    console.error('Failed to create signed URL:', error);
    return null;
  }

  return data.signedUrl;
}
